/**
 * ?Notification Component
 */

import { XMarkIcon } from '@heroicons/react/20/solid';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { FC } from 'react';
import { Fragment, useState } from 'react';

import * as allStyles from './NotificationStyles';

export type NotificationProps = {
  /**
   * Description of themes in Storybook
   */
  theme?: 'theme_default';
  /**
   * Additional Class Names
   */
  classNames?: string;
  /**
   * Class Name override
   */
  customTheme?: any;

  /**
   * Click handler
   */
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;

  /**
   * Submit handler
   */
  onSubmit?: (data: any) => void;

  variant?: string;

  heading?: string;

  content?: string;

  hasIcon?: boolean;

  icon?: string;

  showButtons?: boolean;

  /**
   * Optional callback example
   */
  onNotificationClose?: (data: any) => void;
};

/**
 * Description of Notification component displayed in Storybook
 */

const Notification: FC<NotificationProps> = ({
  theme = 'theme_default',
  classNames = '',
  onClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {},
  customTheme,
  variant,
  heading,
  content,
  hasIcon,
  showButtons = false,
  onNotificationClose = (event: any) => {},

  ...props
}) => {
  const [show, setShow] = useState(true);
  let selectedTheme: any = allStyles.themes[theme];
  selectedTheme = customTheme ?? selectedTheme;

  const getBgColors = () => {
    let color: any = '';
    switch (variant) {
      case 'success':
        color = 'bg-[#F0FDF5]';
        break;
      case 'warning':
        color = 'bg-[#FEFCEA]';
        break;
      case 'detail':
        color = 'bg-[#EFF6FD]';
        break;
      case 'error':
        color = 'bg-[#FEF2F2]';
        break;
    }
    return color;
  };

  const getTextColors = () => {
    let color: any = '';
    switch (variant) {
      case 'success':
        color = 'text-[#0E7C2C]';
        break;
      case 'warning':
        color = 'text-[#9B5A0D]';
        break;
      case 'detail':
        color = 'text-[#1F5E99]';
        break;
      case 'error':
        color = 'text-[#9B2E0D]';
        break;
    }
    return color;
  };

  return (
    <>
      <div
        aria-live="assertive"
        className="pointer-events-none fixed z-10 inset-0 flex items-end px-4 py-6 sm:items-start sm:p-6"
      >
        <div className="flex w-full flex-col items-center space-y-4">
          {show && (
            <Fragment>
              <div
                className={[
                  'pointer-events-auto w-3/5 overflow-hidden rounded-lg z-1 shadow-lg ring-1 ring-black ring-opacity-5',
                  getBgColors(),
                ].join(' ')}
              >
                <div className="p-4">
                  <div className="flex items-start">
                    {hasIcon && (
                      <div className="flex-shrink-0">
                        {variant === 'success' ? (
                          <CheckCircleIcon className="h-6 w-6 text-green-400" aria-hidden="true" />
                        ) : (
                          <ExclamationCircleIcon className="h-6 w-6 text-[#9B5A0D]" aria-hidden="true" />
                        )}
                      </div>
                    )}
                    <div className="ml-3 w-0 flex-1 pt-0.5">
                      <p className={['text-sm font-medium', getTextColors()].join(' ')}>{heading}</p>
                      <p className={['mt-1 text-sm ', getTextColors()].join(' ')}>{content}</p>
                    </div>
                    {showButtons && (
                      <div className="mt-3 flex space-x-7">
                        <button
                          type="button"
                          className="rounded-md bg-white text-sm font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                        >
                          View
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShow(false);
                            onNotificationClose(false);
                          }}
                          className="rounded-md bg-white text-sm font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                        >
                          Dismiss
                        </button>
                      </div>
                    )}
                    <div className="ml-4 flex flex-shrink-0">
                      <button
                        type="button"
                        className="inline-flex rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                        onClick={() => {
                          setShow(false);
                          onNotificationClose(false);
                        }}
                      >
                        <span className="sr-only">Close</span>
                        <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </Fragment>
          )}
        </div>
      </div>
    </>
  );
};

export default Notification;
